/** TARIFS — une seule offre, deux façons de la payer.
 *  Page de conversion : les arguments d'abord, le prix ensuite, aucune option cachée. */
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Btn, Micro } from "../components/Bits";
import { Marque } from "../components/Shell";

const INCLUS = [
  "Rendez-vous et clientes illimités",
  "Ta page de réservation à tes couleurs, sans notre logo",
  "Rappels, confirmations et acomptes automatiques",
  "Fiches clientes : photos, notes techniques, préférences",
  "Ta palette et tes visuels Instagram, générés depuis tes photos",
  "Cinq publications par semaine, déjà écrites",
  "La Voix : tu dictes, ton agenda suit",
  "Ce que chaque prestation te rapporte à l’heure",
  "L’espace de tes clientes, sans qu’elles créent de compte",
];

const ARGUMENTS = [
  [
    "3 outils",
    "En une seule ligne sur ton relevé",
    "Ailleurs : un logiciel d’agenda, une messagerie de rappels, et quelqu’un pour tes visuels. Trois factures, trois mots de passe. Ici, une ligne à 19,90 €.",
  ],
  [
    "+ 55 €",
    "Une absence évitée, et le mois est payé",
    "Rappel la veille, confirmation en un geste, acompte quand tu le décides. Le créneau tient, la prestation est encaissée.",
  ],
  [
    "+ 3 h",
    "Par semaine, sans trier un seul message",
    "Elles réservent seules sur ton lien, à n’importe quelle heure. Tu récupères tes dimanches soir.",
  ],
  [
    "0 %",
    "De commission, aujourd’hui et dans deux ans",
    "Aucun annuaire, aucune mise en concurrence avec le salon d’à côté. Tes clientes sont à toi et tes fiches s’exportent en un clic.",
  ],
];

const FRANCHISE = [
  ["Ce que ça ne fait pas", "Servae ne publie pas à ta place sur Instagram et ne répond pas seul à tes clientes : il prépare, tu valides."],
  ["Si tu arrêtes", "Un clic, sans justification. Tu exportes tes fiches et ton historique, ils sont à toi."],
  ["Si tu débutes", "14 jours complets, sans carte bancaire. Si ton agenda ne se remplit pas, tu n’as rien payé."],
];

export default function Tarifs() {
  const [annuel, setAnnuel] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Helmet>
        <title>Tarifs — Servae, 19,90 €/mois, une seule offre</title>
        <meta
          name="description"
          content="Une offre unique à 19,90 €/mois ou 199 €/an : agenda, page de réservation, rappels, acomptes, fiches clientes et visuels Instagram inclus. 14 jours d'essai sans carte."
        />
        <link rel="canonical" href="https://servae.fr/tarifs" />
      </Helmet>
      <header style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="colonne pad h-14 flex items-center justify-between">
          <Link to="/" style={{ color: "var(--text)" }}>
            <Marque lien={false} />
          </Link>
          <Link to="/inscription">
            <Btn taille="sm" data-testid="tarifs-inscription">
              Essayer 14 jours
            </Btn>
          </Link>
        </div>
      </header>

      <div className="colonne pad py-12 md:py-20">
        <Micro>Tarif</Micro>
        <h1 className="display text-[36px] sm:text-[46px] md:text-[56px] mt-3" style={{ maxWidth: "22ch" }}>
          Un seul prix.
          <br />
          <span className="display-italic">Tout est dedans, vraiment.</span>
        </h1>
        <p className="text-[17px] md:text-[19px] mt-6" style={{ color: "var(--text-muted)", maxWidth: "52ch" }}>
          Pas de version bridée, pas de fonction réservée à un plan supérieur, pas de
          commission sur tes rendez-vous. Tu paies un abonnement, tu as l’outil entier.
        </p>

        <div className="mt-12 md:mt-16 grid lg:grid-cols-[1fr_420px] gap-10 lg:gap-16 items-start">
          <div className="space-y-8">
            {ARGUMENTS.map(([n, t, d]) => (
              <div key={t} className="pl-5" style={{ borderLeft: "2px solid var(--accent)" }}>
                <p className="num text-[24px] md:text-[30px] leading-none" style={{ color: "var(--accent)" }}>
                  {n}
                </p>
                <h2 className="mt-3 text-[18px] md:text-[21px] font-semibold" style={{ letterSpacing: "-0.015em" }}>
                  {t}
                </h2>
                <p className="mt-2 text-[16px]" style={{ color: "var(--text-muted)", maxWidth: "48ch" }}>
                  {d}
                </p>
              </div>
            ))}
          </div>

          <div>
            <div
              className="p-7 md:p-8"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--accent)",
                borderRadius: "var(--radius-xl)",
                boxShadow: "var(--shadow-md)",
              }}
              data-testid="carte-prix"
            >
              <div
                className="flex p-1 gap-1 w-fit"
                style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-full)" }}
              >
                {[
                  { v: false, l: "Au mois" },
                  { v: true, l: "À l’année" },
                ].map((o) => (
                  <button
                    key={o.l}
                    onClick={() => setAnnuel(o.v)}
                    data-testid={`tarif-${o.v ? "annuel" : "mensuel"}`}
                    className="h-8 px-3.5 text-[12.5px] font-semibold"
                    style={{
                      borderRadius: "var(--radius-full)",
                      background: annuel === o.v ? "var(--surface)" : "transparent",
                      color: annuel === o.v ? "var(--text)" : "var(--text-subtle)",
                      boxShadow: annuel === o.v ? "var(--shadow-sm)" : "none",
                    }}
                  >
                    {o.l}
                  </button>
                ))}
              </div>

              <p className="flex items-baseline gap-2 mt-5">
                <span className="num" style={{ fontSize: 48, lineHeight: 1, fontWeight: 500 }}>
                  {annuel ? "199 €" : "19,90 €"}
                </span>
                <span className="text-[15px]" style={{ color: "var(--text-muted)" }}>
                  {annuel ? "/ an" : "/ mois"}
                </span>
              </p>
              <p className="mt-2 text-[14.5px]" style={{ color: annuel ? "var(--accent)" : "var(--text-muted)" }}>
                {annuel
                  ? "Deux mois offerts, soit 16,58 € par mois."
                  : "Ou 199 € par an — deux mois offerts."}
              </p>

              <p className="micro mt-7 pb-2.5" style={{ borderBottom: "1px solid var(--border)" }}>
                Tout est dedans
              </p>
              <ul className="mt-4 space-y-2.5">
                {INCLUS.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[15px]">
                    <Check
                      size={16}
                      strokeWidth={2.2}
                      style={{ color: "var(--accent)", marginTop: 4, flexShrink: 0 }}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link to="/inscription" className="block mt-7">
                <Btn taille="lg" className="w-full" data-testid="cta-tarifs">
                  Essayer 14 jours
                </Btn>
              </Link>
              <p className="mt-3 text-[13px]" style={{ color: "var(--text-subtle)" }}>
                Sans carte bancaire. Sans engagement.
              </p>
            </div>

            <div className="mt-8 space-y-5">
              {FRANCHISE.map(([t, d]) => (
                <div key={t}>
                  <p className="text-[14.5px] font-semibold">{t}</p>
                  <p className="text-[14.5px] mt-1" style={{ color: "var(--text-muted)" }}>
                    {d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
